import AssessmentIcon from "@mui/icons-material/Assessment";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import SpeedIcon from "@mui/icons-material/Speed";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAccount, useToast } from "../../../context";
import { useApiError } from "../../../hooks/useApiError";
import { Account, StatusColor } from "../../../interfaces/all";
import {
  fetchWhatsAppInfo,
  getCompanyData,
  refreshMetaLimits,
  requestPhoneVerificationCode,
  setRemainingToMetaTier,
  updateMetaAppConfig,
  updatePhoneNumberId,
  updateWabaId,
  verifyPhoneVerificationCode,
} from "../../../services/api";
import { ErrorHandler } from "../../../utils/error-handler";
import Header from "../../chat/Header/Header";

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const BoxWrapper = styled(Box)`
  background: #ffffff;
  padding: 12px 20px;
  margin-bottom: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled(Typography)`
  font-size: 13px;
  color: #008069;
  font-weight: 500;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const inputFieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: "#008069" },
    "&.Mui-focused fieldset": { borderColor: "#008069" },
  },
} as const;

const dialogPaperSx = {
  borderRadius: "12px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
  "@media (max-width: 600px)": { margin: "8px", maxWidth: "calc(100% - 16px)" },
};

const getStatusColor = (status: string | undefined): StatusColor => {
  if (!status) return "info";
  const statusUpper = status.toUpperCase();
  switch (statusUpper) {
    case "VERIFIED":
      return "success";
    case "PENDING":
    case "IN_REVIEW":
      return "warning";
    case "REJECTED":
    case "EXPIRED":
      return "error";
    case "UNVERIFIED":
    case "NOT_VERIFIED":
      return "info";
    case "GREEN":
      return "success";
    case "YELLOW":
      return "warning";
    case "RED":
      return "error";
    case "UNKNOWN":
      return "info";
    default:
      return "info";
  }
};

const getStatusStyles = (status: string | undefined) => {
  const colorMap: Record<string, { bg: string; border: string; text: string }> =
    {
      success: { bg: "#e0f2f1", border: "#00806930", text: "#008069" },
      warning: { bg: "#fff3e0", border: "#f57c0030", text: "#f57c00" },
      error: { bg: "#fbe9e7", border: "#d32f2f30", text: "#d32f2f" },
      info: { bg: "#e3f2fd", border: "#1976d230", text: "#1976d2" },
    };
  const color = getStatusColor(status);
  if (!status) {
    return { bg: "#f0f2f5", border: "#e9edef", text: "#667781" };
  }
  return colorMap[color] || colorMap.info;
};

const CompanySettings = () => {
  const { account, setAccount } = useAccount();
  const { showError, showSuccess } = useToast();
  const { handleApiCall } = useApiError();
  const [wabaId, setWabaId] = useState("");
  const [savingWabaId, setSavingWabaId] = useState(false);
  const [openWabaModal, setOpenWabaModal] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [savingPhoneNumberId, setSavingPhoneNumberId] = useState(false);
  const [openPhoneNumberIdModal, setOpenPhoneNumberIdModal] = useState(false);
  const [openVerificationModal, setOpenVerificationModal] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState<"SMS" | "VOICE">(
    "SMS",
  );
  const [verificationCode, setVerificationCode] = useState("");
  const [requestingVerificationCode, setRequestingVerificationCode] =
    useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [verificationCodeRequested, setVerificationCodeRequested] =
    useState(false);
  const [refreshingMetaLimits, setRefreshingMetaLimits] = useState(false);
  const [settingRemainingToTier, setSettingRemainingToTier] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showVerifyToken, setShowVerifyToken] = useState(false);
  const [openAppVersionModal, setOpenAppVersionModal] = useState(false);
  const [openAccessTokenModal, setOpenAccessTokenModal] = useState(false);
  const [openVerifyTokenModal, setOpenVerifyTokenModal] = useState(false);
  const [openAppIdentifyModal, setOpenAppIdentifyModal] = useState(false);
  const [editAppVersion, setEditAppVersion] = useState("");
  const [editAccessToken, setEditAccessToken] = useState("");
  const [editVerifyToken, setEditVerifyToken] = useState("");
  const [editAppIdentify, setEditAppIdentify] = useState("");
  const [savingAppVersion, setSavingAppVersion] = useState(false);
  const [savingAccessToken, setSavingAccessToken] = useState(false);
  const [savingVerifyToken, setSavingVerifyToken] = useState(false);
  const [savingAppIdentify, setSavingAppIdentify] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Limpar erro ao mudar de aba ou fechar, se necessário.
  useEffect(() => {
    return () => setError(null);
  }, []);

  useEffect(() => {
    const wabaAccounts =
      account?.company?.meta?.whatsapp_business_accounts?.data;
    if (
      Array.isArray(wabaAccounts) &&
      wabaAccounts.length > 0 &&
      wabaAccounts[0]?.id
    ) {
      setWabaId(wabaAccounts[0].id);
    } else {
      setWabaId("");
    }
  }, [account]);

  const handleOpenWabaModal = () => {
    const wabaAccounts =
      account?.company?.meta?.whatsapp_business_accounts?.data;
    if (
      Array.isArray(wabaAccounts) &&
      wabaAccounts.length > 0 &&
      wabaAccounts[0]?.id
    ) {
      setWabaId(wabaAccounts[0].id);
    } else {
      setWabaId("");
    }
    setOpenWabaModal(true);
  };

  const handleCloseWabaModal = () => setOpenWabaModal(false);

  const handleOpenPhoneNumberIdModal = () => {
    const current =
      account?.company?.meta?.phone_numbers?.find(
        (p) => p.objective === "marketing",
      )?.id ||
      account?.company?.meta?.whatsapp_business_info?.id ||
      "";
    setPhoneNumberId(current);
    setOpenPhoneNumberIdModal(true);
  };

  const handleClosePhoneNumberIdModal = () => setOpenPhoneNumberIdModal(false);

  const handleOpenVerificationModal = () => {
    setVerificationMethod("SMS");
    setVerificationCode("");
    setVerificationCodeRequested(false);
    setOpenVerificationModal(true);
  };

  const handleCloseVerificationModal = () => {
    if (requestingVerificationCode || verifyingCode) return;
    setOpenVerificationModal(false);
    setVerificationCode("");
    setVerificationCodeRequested(false);
  };

  const handleSaveWabaId = async () => {
    if (!account || !wabaId.trim()) {
      showError("Por favor, informe o WABA_ID");
      return;
    }
    setSavingWabaId(true);
    await handleApiCall(
      async () => {
        const companyId =
          account.company.crm?.company_id || account.company._id;
        await updateWabaId(companyId, wabaId.trim());
        const updated = await getCompanyData(account.company_id);
        if (updated.status === "success" && updated.data && setAccount) {
          const newCompany = updated.data as unknown as Account["company"];
          setAccount((prev) =>
            prev ? { ...prev, company: newCompany } : prev,
          );
        }
        return { status: "success" as const };
      },
      {
        onSuccess: () => {
          showSuccess("WABA_ID atualizado com sucesso!");
          handleCloseWabaModal();
        },
        onError: () => setSavingWabaId(false),
        fallbackErrorMessage: "Erro ao atualizar WABA_ID. Tente novamente.",
        successMessage: undefined,
      },
    );
    setSavingWabaId(false);
  };

  const handleSavePhoneNumberId = async () => {
    if (!account || !phoneNumberId.trim()) {
      showError("Por favor, informe o PHONE_NUMBER_ID");
      return;
    }
    setSavingPhoneNumberId(true);
    await handleApiCall(
      async () => {
        const companyId =
          account.company.crm?.company_id || account.company._id;
        await updatePhoneNumberId(companyId, phoneNumberId.trim());
        const updated = await getCompanyData(account.company_id);
        if (updated.status === "success" && updated.data && setAccount) {
          const newCompany = updated.data as unknown as Account["company"];
          setAccount((prev) =>
            prev ? { ...prev, company: newCompany } : prev,
          );
        }
        return { status: "success" as const };
      },
      {
        onSuccess: () => {
          showSuccess("PHONE_NUMBER_ID atualizado com sucesso!");
          handleClosePhoneNumberIdModal();
        },
        onError: () => setSavingPhoneNumberId(false),
        fallbackErrorMessage:
          "Erro ao atualizar PHONE_NUMBER_ID. Tente novamente.",
        successMessage: undefined,
      },
    );
    setSavingPhoneNumberId(false);
  };

  const handleRequestVerificationCode = async () => {
    if (!account) return;
    const companyId = account.company.crm?.company_id || account.company_id;
    if (!companyId) {
      showError("Não foi possível identificar a empresa para verificação.");
      return;
    }
    setRequestingVerificationCode(true);
    await handleApiCall(
      async () => requestPhoneVerificationCode(companyId, verificationMethod),
      {
        onSuccess: () => {
          setVerificationCodeRequested(true);
          showSuccess(
            "Código de verificação solicitado. Verifique seu SMS ou ligação.",
          );
        },
        onError: () => setRequestingVerificationCode(false),
        fallbackErrorMessage:
          "Erro ao solicitar código de verificação. Tente novamente.",
        successMessage: undefined,
      },
    );
    setRequestingVerificationCode(false);
  };

  const handleVerifyCode = async () => {
    if (!account || !verificationCode.trim()) {
      showError("Informe o código recebido.");
      return;
    }
    const companyId = account.company.crm?.company_id || account.company_id;
    if (!companyId) {
      showError("Não foi possível identificar a empresa para verificação.");
      return;
    }
    setVerifyingCode(true);
    await handleApiCall(
      async () =>
        verifyPhoneVerificationCode(companyId, verificationCode.trim()),
      {
        onSuccess: () => {
          showSuccess("Código verificado com sucesso!");
          handleCloseVerificationModal();
          getCompanyData(account.company_id).then((updated) => {
            if (updated.status === "success" && updated.data && setAccount) {
              const newCompany = updated.data as unknown as Account["company"];
              setAccount((prev) =>
                prev ? { ...prev, company: newCompany } : prev,
              );
            }
          });
        },
        onError: () => setVerifyingCode(false),
        fallbackErrorMessage:
          "Erro ao verificar código. Confira e tente novamente.",
        successMessage: undefined,
      },
    );
    setVerifyingCode(false);
  };

  if (!account?.company?.name) {
    return (
      <Container>
        <Box sx={{ height: "60px", flexShrink: 0 }}>
          <Header title="Configurações" />
        </Box>
        <ContentContainer>
          <Box sx={{ p: 2 }}>
            <Typography color="textSecondary">Carregando...</Typography>
          </Box>
        </ContentContainer>
      </Container>
    );
  }

  const whatsappBusinessInfo = account.company.meta?.whatsapp_business_info || {
    code_verification_status: "",
    quality_rating: "",
    platform_type: "",
    throughput: { level: "" },
    display_phone_number: "",
    verified_name: "",
    id: "",
  };

  const currentWabaId =
    account?.company?.meta?.whatsapp_business_accounts &&
    typeof account.company.meta.whatsapp_business_accounts === "object" &&
    "data" in account.company.meta.whatsapp_business_accounts &&
    Array.isArray(account.company.meta.whatsapp_business_accounts.data) &&
    account.company.meta.whatsapp_business_accounts.data.length > 0
      ? (
          account.company.meta.whatsapp_business_accounts.data[0] as {
            id?: string;
          }
        )?.id
      : null;

  const marketingPhoneNumberId =
    account.company.meta?.phone_numbers?.find(
      (p) => p.objective === "marketing",
    )?.id || null;
  const phoneNumberIdForVerification =
    marketingPhoneNumberId || whatsappBusinessInfo?.id || null;
  const wabaIdForVerification = currentWabaId || null;
  const isAdministrator =
    account.role === "ADMINISTRATOR" ||
    account.role === "administrator" ||
    account.role?.toUpperCase() === "ADMINISTRATOR";

  const appVersion = account.company.meta?.app_version || "";
  const accessToken = account.company.meta?.access_token || "";
  const verifyToken = account.company.meta?.verify_token || "";
  const appIdentify = account.company.meta?.app_identify || "";

  const maskToken = (value: string) => {
    if (!value) return "Não configurado";
    if (value.length <= 8) return "••••••••";
    return `${value.slice(0, 4)}••••${value.slice(-4)}`;
  };

  return (
    <Container>
      <Box sx={{ height: "60px", flexShrink: 0 }}>
        <Header title="Configurações" />
      </Box>
      <ContentContainer>
        {error && (
          <Alert
            severity="error"
            sx={{ m: 2, borderRadius: "8px" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {isAdministrator && (
          <BoxWrapper>
            <SectionTitle>
              <SettingsIcon fontSize="small" />
              Admin (Meta)
            </SectionTitle>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* APP_IDENTIFY */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{ fontSize: "11px", color: "#667781", fontWeight: 500 }}
                  >
                    APP_IDENTIFY
                  </Typography>
                  <Tooltip
                    title="Identificador interno da aplicação (nome/código do app no painel da Meta)."
                    arrow
                    placement="top"
                  >
                    <InfoIcon
                      sx={{
                        fontSize: "12px",
                        color: "#667781",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label={appIdentify || "Não configurado"}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      height: "22px",
                      backgroundColor: appIdentify ? "#e0f2f1" : "#f0f2f5",
                      color: appIdentify ? "#008069" : "#667781",
                      "& .MuiChip-label": { padding: "0 8px" },
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      setEditAppIdentify(appIdentify || "");
                      setOpenAppIdentifyModal(true);
                    }}
                    startIcon={<EditIcon sx={{ fontSize: "14px" }} />}
                    sx={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      color: "#008069",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#f0f7f6" },
                    }}
                  >
                    {appIdentify ? "Editar" : "Configurar"}
                  </Button>
                </Box>
              </Box>

              {/* APP_VERSION */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{ fontSize: "11px", color: "#667781", fontWeight: 500 }}
                  >
                    APP_VERSION
                  </Typography>
                  <Tooltip
                    title="Versão da API do WhatsApp utilizada nas chamadas (ex.: v21.0)."
                    arrow
                    placement="top"
                  >
                    <InfoIcon
                      sx={{
                        fontSize: "12px",
                        color: "#667781",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label={appVersion || "Não configurado"}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      height: "22px",
                      backgroundColor: appVersion ? "#e0f2f1" : "#f0f2f5",
                      color: appVersion ? "#008069" : "#667781",
                      "& .MuiChip-label": { padding: "0 8px" },
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      setEditAppVersion(appVersion || "");
                      setOpenAppVersionModal(true);
                    }}
                    startIcon={<EditIcon sx={{ fontSize: "14px" }} />}
                    sx={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      color: "#008069",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#f0f7f6" },
                    }}
                  >
                    {appVersion ? "Editar" : "Configurar"}
                  </Button>
                </Box>
              </Box>

              {/* ACCESS_TOKEN */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{ fontSize: "11px", color: "#667781", fontWeight: 500 }}
                  >
                    ACCESS_TOKEN
                  </Typography>
                  <Tooltip
                    title="Token de acesso da Meta usado para autenticar chamadas à API do WhatsApp."
                    arrow
                    placement="top"
                  >
                    <InfoIcon
                      sx={{
                        fontSize: "12px",
                        color: "#667781",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label={maskToken(accessToken)}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      height: "22px",
                      backgroundColor: accessToken ? "#e0f2f1" : "#f0f2f5",
                      color: accessToken ? "#008069" : "#667781",
                      "& .MuiChip-label": { padding: "0 8px" },
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      setEditAccessToken(accessToken || "");
                      setOpenAccessTokenModal(true);
                    }}
                    startIcon={<EditIcon sx={{ fontSize: "14px" }} />}
                    sx={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      color: "#008069",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#f0f7f6" },
                    }}
                  >
                    {accessToken ? "Editar" : "Configurar"}
                  </Button>
                </Box>
              </Box>

              {/* VERIFY_TOKEN */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{ fontSize: "11px", color: "#667781", fontWeight: 500 }}
                  >
                    VERIFY_TOKEN
                  </Typography>
                  <Tooltip
                    title="Token de verificação usado na configuração do webhook da Meta."
                    arrow
                    placement="top"
                  >
                    <InfoIcon
                      sx={{
                        fontSize: "12px",
                        color: "#667781",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label={maskToken(verifyToken)}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      height: "22px",
                      backgroundColor: verifyToken ? "#e0f2f1" : "#f0f2f5",
                      color: verifyToken ? "#008069" : "#667781",
                      "& .MuiChip-label": { padding: "0 8px" },
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      setEditVerifyToken(verifyToken || "");
                      setOpenVerifyTokenModal(true);
                    }}
                    startIcon={<EditIcon sx={{ fontSize: "14px" }} />}
                    sx={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      color: "#008069",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#f0f7f6" },
                    }}
                  >
                    {verifyToken ? "Editar" : "Configurar"}
                  </Button>
                </Box>
              </Box>

              {/* WABA_ID */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{ fontSize: "11px", color: "#667781", fontWeight: 500 }}
                  >
                    WABA_ID
                  </Typography>
                  <Tooltip
                    title="WhatsApp Business Account ID (WABA_ID) é necessário para buscar templates do WhatsApp."
                    arrow
                    placement="top"
                  >
                    <InfoIcon
                      sx={{
                        fontSize: "12px",
                        color: "#667781",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label={currentWabaId || "Não configurado"}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      height: "22px",
                      backgroundColor: currentWabaId ? "#e0f2f1" : "#f0f2f5",
                      color: currentWabaId ? "#008069" : "#667781",
                      "& .MuiChip-label": { padding: "0 8px" },
                    }}
                  />
                  <Button
                    size="small"
                    onClick={handleOpenWabaModal}
                    startIcon={<EditIcon sx={{ fontSize: "14px" }} />}
                    sx={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      color: "#008069",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#f0f7f6" },
                    }}
                  >
                    {currentWabaId ? "Editar" : "Configurar"}
                  </Button>
                </Box>
              </Box>

              {/* PHONE_NUMBER_ID */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{ fontSize: "11px", color: "#667781", fontWeight: 500 }}
                  >
                    PHONE_NUMBER_ID
                  </Typography>
                  <Tooltip
                    title="PHONE_NUMBER_ID é o ID do número no WhatsApp Cloud API (Meta). Use somente se você precisar corrigir manualmente."
                    arrow
                    placement="top"
                  >
                    <InfoIcon
                      sx={{
                        fontSize: "12px",
                        color: "#667781",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label={phoneNumberIdForVerification || "Não configurado"}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      height: "22px",
                      backgroundColor: phoneNumberIdForVerification
                        ? "#e0f2f1"
                        : "#f0f2f5",
                      color: phoneNumberIdForVerification
                        ? "#008069"
                        : "#667781",
                      "& .MuiChip-label": { padding: "0 8px" },
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {phoneNumberIdForVerification && (
                      <Button
                        size="small"
                        onClick={handleOpenVerificationModal}
                        startIcon={<CheckIcon sx={{ fontSize: "14px" }} />}
                        sx={{
                          fontSize: "11px",
                          padding: "2px 8px",
                          color: "#008069",
                          textTransform: "none",
                          "&:hover": { backgroundColor: "#f0f7f6" },
                        }}
                      >
                        Verificar
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={handleOpenPhoneNumberIdModal}
                      startIcon={<EditIcon sx={{ fontSize: "14px" }} />}
                      sx={{
                        fontSize: "11px",
                        padding: "2px 8px",
                        color: "#008069",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#f0f7f6" },
                      }}
                    >
                      {phoneNumberIdForVerification ? "Editar" : "Configurar"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </BoxWrapper>
        )}

        <BoxWrapper>
          <SectionTitle>
            <SpeedIcon fontSize="small" />
            Status do Negócio
          </SectionTitle>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 1,
              mt: 1,
            }}
          >
            {[
              {
                label: "Verificação",
                value: whatsappBusinessInfo?.code_verification_status,
                tooltip:
                  "Status de verificação do código de negócio do WhatsApp Business API.",
              },
              {
                label: "Qualidade",
                value: whatsappBusinessInfo?.quality_rating,
                tooltip:
                  "Classificação de qualidade do negócio baseada no comportamento de mensagens enviadas.",
              },
              {
                label: "Plataforma",
                value: whatsappBusinessInfo?.platform_type,
                tooltip:
                  "Tipo de plataforma usada para integração com o WhatsApp Business API.",
              },
              {
                label: "Capacidade",
                value: whatsappBusinessInfo?.throughput?.level,
                tooltip:
                  "Nível de throughput (capacidade) de mensagens por segundo.",
              },
            ].map(({ label, value, tooltip }) => (
              <Box
                key={label}
                sx={{
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: getStatusStyles(value).bg,
                  border: `1px solid ${getStatusStyles(value).border}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "11px", color: "#667781", fontWeight: 500 }}
                  >
                    {label}
                  </Typography>
                  <Tooltip title={tooltip} arrow placement="top">
                    <InfoIcon
                      sx={{
                        fontSize: "12px",
                        color: "#667781",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                </Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: getStatusStyles(value).text,
                  }}
                >
                  {value || "Não especificado"}
                </Typography>
              </Box>
            ))}
          </Box>
        </BoxWrapper>

        <BoxWrapper>
          <SectionTitle>
            <AssessmentIcon fontSize="small" />
            Limites de mensagens
            <Tooltip
              title="Conversas iniciadas pela empresa (fora da janela de 24h) consomem a cota. O valor é conferido com o limite da Meta e com a tabela de envios."
              arrow
              placement="top"
            >
              <InfoIcon
                sx={{ fontSize: "14px", color: "#667781", cursor: "help" }}
              />
            </Tooltip>
          </SectionTitle>
          <Box
            sx={{
              mt: 1,
              p: 2,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)",
              border: "1px solid rgba(46, 125, 50, 0.2)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-end",
                gap: 2,
                rowGap: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#558b2f",
                    fontWeight: 600,
                    mb: 0.25,
                    letterSpacing: "0.02em",
                  }}
                >
                  Restantes hoje
                </Typography>
                <Typography
                  sx={{
                    fontSize: "28px",
                    fontWeight: 700,
                    color: "#2e7d32",
                    lineHeight: 1.2,
                  }}
                >
                  {account.company.meta?.limitation?.max_messages_per_day !=
                  null
                    ? Number(
                        account.company.meta.limitation.max_messages_per_day,
                      ).toLocaleString("pt-BR")
                    : "—"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.5,
                  pb: 0.25,
                }}
              >
                <Typography
                  component="span"
                  sx={{ fontSize: "13px", color: "#667781", fontWeight: 500 }}
                >
                  de
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontSize: "16px", fontWeight: 600, color: "#1565c0" }}
                >
                  {account.company.meta?.limitation
                    ?.messaging_limit_tier_from_meta != null &&
                  account.company.meta.limitation
                    .messaging_limit_tier_from_meta !== ""
                    ? Number(
                        account.company.meta.limitation
                          .messaging_limit_tier_from_meta,
                      ).toLocaleString("pt-BR")
                    : "—"}
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontSize: "12px", color: "#667781" }}
                >
                  /dia (limite Meta)
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 1.5,
                pt: 1.5,
                borderTop: "1px solid rgba(46, 125, 50, 0.15)",
              }}
            >
              <Button
                size="small"
                variant="contained"
                disabled={settingRemainingToTier}
                startIcon={
                  settingRemainingToTier ? null : (
                    <RefreshIcon sx={{ fontSize: "14px" }} />
                  )
                }
                onClick={async () => {
                  const companyId =
                    account.company.crm?.company_id || account.company_id;
                  if (!companyId || settingRemainingToTier) return;
                  setSettingRemainingToTier(true);
                  await handleApiCall(
                    async () => {
                      await setRemainingToMetaTier(companyId);
                      const updated = await getCompanyData(account.company_id);
                      if (
                        updated.status === "success" &&
                        updated.data &&
                        setAccount
                      ) {
                        const newCompany =
                          updated.data as unknown as Account["company"];
                        setAccount((prev) =>
                          prev ? { ...prev, company: newCompany } : prev,
                        );
                      }
                      return { status: "success" as const };
                    },
                    {
                      onSuccess: (res: any) => {
                        const msg = res?.data?.message;
                        showSuccess(
                          typeof msg === "string"
                            ? msg
                            : "Limites diários sincronizados.",
                        );
                        setError(null);
                      },
                      onError: (err) => {
                        const errorResponse = ErrorHandler.handleApiError(err);
                        setError(
                          errorResponse.userMessage ||
                            errorResponse.message ||
                            "Erro ao sincronizar limites",
                        );
                        setSettingRemainingToTier(false);
                      },
                      fallbackErrorMessage:
                        "Não foi possível atualizar os limites diários.",
                      successMessage: undefined,
                    },
                  );
                  setSettingRemainingToTier(false);
                }}
                sx={{
                  fontSize: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#2e7d32",
                  px: 1.5,
                  "&:hover": { backgroundColor: "#1b5e20" },
                  "&.Mui-disabled": { opacity: 0.8 },
                }}
              >
                {settingRemainingToTier ? "Sincronizando…" : "Limites diários"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={refreshingMetaLimits || settingRemainingToTier}
                startIcon={
                  refreshingMetaLimits ? null : (
                    <RefreshIcon sx={{ fontSize: "14px" }} />
                  )
                }
                onClick={async () => {
                  const companyId =
                    account.company.crm?.company_id || account.company_id;
                  if (!companyId || refreshingMetaLimits) return;
                  setRefreshingMetaLimits(true);
                  await handleApiCall(
                    async () => {
                      await refreshMetaLimits(companyId);
                      const updated = await getCompanyData(account.company_id);
                      if (
                        updated.status === "success" &&
                        updated.data &&
                        setAccount
                      ) {
                        const newCompany =
                          updated.data as unknown as Account["company"];
                        setAccount((prev) =>
                          prev ? { ...prev, company: newCompany } : prev,
                        );
                      }
                      return { status: "success" as const };
                    },
                    {
                      onSuccess: () => {
                        showSuccess("Limites da conta atualizados.");
                        setError(null);
                      },
                      onError: (err) => {
                        const errorResponse = ErrorHandler.handleApiError(err);
                        setError(
                          errorResponse.userMessage ||
                            errorResponse.message ||
                            "Erro ao atualizar limites da conta",
                        );
                        setRefreshingMetaLimits(false);
                      },
                      fallbackErrorMessage:
                        "Não foi possível atualizar os limites da conta.",
                      successMessage: undefined,
                    },
                  );
                  setRefreshingMetaLimits(false);
                }}
                sx={{
                  fontSize: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#1565c0",
                  borderColor: "rgba(21, 101, 192, 0.5)",
                  px: 1.5,
                  "&:hover": {
                    borderColor: "#1565c0",
                    backgroundColor: "rgba(21, 101, 192, 0.06)",
                  },
                  "&.Mui-disabled": { opacity: 0.7 },
                }}
              >
                {refreshingMetaLimits ? "Atualizando…" : "Limites da conta"}
              </Button>
            </Box>
            {account.company.meta?.limitation?.last_checked && (
              <Typography sx={{ fontSize: "11px", color: "#667781", mt: 1.25 }}>
                Atualizado em{" "}
                {(() => {
                  const raw = account.company.meta!.limitation!.last_checked;
                  try {
                    const d = new Date(raw);
                    if (!Number.isNaN(d.getTime())) {
                      return d.toLocaleString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                    }
                  } catch {
                    // ignore
                  }
                  return raw;
                })()}
              </Typography>
            )}
          </Box>
        </BoxWrapper>

        {/* Modal Verificação (Meta) */}
        <Dialog
          open={openVerificationModal}
          onClose={handleCloseVerificationModal}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={requestingVerificationCode || verifyingCode}
          slotProps={{ paper: { sx: dialogPaperSx } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2.5,
              borderBottom: "1px solid #e9edef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SettingsIcon sx={{ color: "#008069", fontSize: "20px" }} />
              <Typography
                variant="h6"
                sx={{ fontSize: "16px", fontWeight: 600, color: "#111b21" }}
              >
                Verificar número (Meta)
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseVerificationModal}
              disabled={requestingVerificationCode || verifyingCode}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#e9edef" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#667781", fontSize: "13px" }}
              >
                Este fluxo solicita um novo código (SMS ou ligação) e confirma o
                código para atualizar o status de verificação.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography sx={{ fontSize: "12px", color: "#667781" }}>
                  PHONE_NUMBER_ID:{" "}
                  <b>{phoneNumberIdForVerification || "não encontrado"}</b>
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#667781" }}>
                  WABA_ID: <b>{wabaIdForVerification || "não encontrado"}</b>
                </Typography>
              </Box>
              <FormControl>
                <RadioGroup
                  row
                  value={verificationMethod}
                  onChange={(e) =>
                    setVerificationMethod(e.target.value as "SMS" | "VOICE")
                  }
                >
                  <FormControlLabel
                    value="SMS"
                    control={<Radio size="small" />}
                    label="SMS"
                  />
                  <FormControlLabel
                    value="VOICE"
                    control={<Radio size="small" />}
                    label="Ligação"
                  />
                </RadioGroup>
              </FormControl>
              <TextField
                label="Código recebido"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Ex: 123456"
                variant="outlined"
                fullWidth
                disabled={
                  !verificationCodeRequested ||
                  requestingVerificationCode ||
                  verifyingCode
                }
                helperText={
                  verificationCodeRequested
                    ? 'Digite o código recebido e clique em "Confirmar"'
                    : 'Clique em "Enviar código" para receber o código'
                }
                sx={inputFieldSx}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ p: 2.5, borderTop: "1px solid #e9edef", gap: 1 }}
          >
            <Button
              onClick={handleCloseVerificationModal}
              disabled={requestingVerificationCode || verifyingCode}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#f0f2f5" },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="outlined"
              onClick={handleRequestVerificationCode}
              disabled={
                !phoneNumberIdForVerification ||
                requestingVerificationCode ||
                verifyingCode
              }
              startIcon={
                requestingVerificationCode ? (
                  <RefreshIcon />
                ) : (
                  <SettingsIcon sx={{ fontSize: "16px" }} />
                )
              }
              sx={{
                borderColor: "#008069",
                color: "#008069",
                "&:hover": {
                  borderColor: "#006d5a",
                  backgroundColor: "#f0f7f6",
                },
              }}
            >
              {requestingVerificationCode ? "Enviando..." : "Enviar código"}
            </Button>
            <Button
              variant="contained"
              onClick={handleVerifyCode}
              disabled={
                !verificationCodeRequested ||
                !verificationCode.trim() ||
                verifyingCode ||
                requestingVerificationCode
              }
              startIcon={verifyingCode ? <RefreshIcon /> : <SaveIcon />}
              sx={{
                backgroundColor: "#008069",
                "&:hover": { backgroundColor: "#006d5a" },
                "&:disabled": { backgroundColor: "#cccccc" },
              }}
            >
              {verifyingCode ? "Confirmando..." : "Confirmar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal APP_VERSION */}
        <Dialog
          open={openAppVersionModal}
          onClose={() => !savingAppVersion && setOpenAppVersionModal(false)}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={savingAppVersion}
          slotProps={{ paper: { sx: dialogPaperSx } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2.5,
              borderBottom: "1px solid #e9edef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SettingsIcon sx={{ color: "#008069", fontSize: "20px" }} />
              <Typography
                variant="h6"
                sx={{ fontSize: "16px", fontWeight: 600, color: "#111b21" }}
              >
                Configurar APP_VERSION
              </Typography>
            </Box>
            <IconButton
              onClick={() => !savingAppVersion && setOpenAppVersionModal(false)}
              disabled={savingAppVersion}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#e9edef" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#667781", fontSize: "13px", mb: 1 }}
              >
                Versão da API do WhatsApp que será usada nas chamadas (ex.:
                v21.0). Certifique-se de que corresponde à versão configurada no
                app da Meta.
              </Typography>
              <TextField
                label="APP_VERSION"
                value={editAppVersion}
                onChange={(e) => setEditAppVersion(e.target.value)}
                placeholder="Ex: v21.0"
                variant="outlined"
                fullWidth
                disabled={savingAppVersion}
                helperText={
                  appVersion
                    ? `APP_VERSION atual: ${appVersion}`
                    : "Informe a versão da API do WhatsApp"
                }
                sx={inputFieldSx}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ p: 2.5, borderTop: "1px solid #e9edef", gap: 1 }}
          >
            <Button
              onClick={() => !savingAppVersion && setOpenAppVersionModal(false)}
              disabled={savingAppVersion}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#f0f2f5" },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!account) return;
                const companyId =
                  account.company.crm?.company_id || account.company._id;
                if (!companyId) return;
                setSavingAppVersion(true);
                await handleApiCall(
                  async () => {
                    await updateMetaAppConfig(companyId, {
                      app_version: editAppVersion.trim() || undefined,
                    });
                    const updated = await getCompanyData(account.company_id);
                    if (
                      updated.status === "success" &&
                      updated.data &&
                      setAccount
                    ) {
                      const newCompany =
                        updated.data as unknown as Account["company"];
                      setAccount((prev) =>
                        prev ? { ...prev, company: newCompany } : prev,
                      );
                    }
                    return { status: "success" as const };
                  },
                  {
                    onSuccess: () => {
                      showSuccess("APP_VERSION atualizado com sucesso!");
                      setOpenAppVersionModal(false);
                    },
                    onError: () => setSavingAppVersion(false),
                    fallbackErrorMessage:
                      "Erro ao atualizar APP_VERSION. Tente novamente.",
                    successMessage: undefined,
                  },
                );
                setSavingAppVersion(false);
              }}
              disabled={savingAppVersion || !editAppVersion.trim()}
              startIcon={savingAppVersion ? <RefreshIcon /> : <SaveIcon />}
              sx={{
                backgroundColor: "#008069",
                "&:hover": { backgroundColor: "#006d5a" },
                "&:disabled": { backgroundColor: "#cccccc" },
              }}
            >
              {savingAppVersion ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal ACCESS_TOKEN */}
        <Dialog
          open={openAccessTokenModal}
          onClose={() => !savingAccessToken && setOpenAccessTokenModal(false)}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={savingAccessToken}
          slotProps={{ paper: { sx: dialogPaperSx } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2.5,
              borderBottom: "1px solid #e9edef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SettingsIcon sx={{ color: "#008069", fontSize: "20px" }} />
              <Typography
                variant="h6"
                sx={{ fontSize: "16px", fontWeight: 600, color: "#111b21" }}
              >
                Configurar ACCESS_TOKEN
              </Typography>
            </Box>
            <IconButton
              onClick={() =>
                !savingAccessToken && setOpenAccessTokenModal(false)
              }
              disabled={savingAccessToken}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#e9edef" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#667781", fontSize: "13px", mb: 1 }}
              >
                Token de acesso gerado no painel da Meta para o aplicativo do
                WhatsApp. Mantenha este valor em sigilo.
              </Typography>
              <TextField
                label="ACCESS_TOKEN"
                value={editAccessToken}
                onChange={(e) => setEditAccessToken(e.target.value)}
                placeholder="Ex: EAAJZ..."
                variant="outlined"
                fullWidth
                disabled={savingAccessToken}
                type={showAccessToken ? "text" : "password"}
                helperText={
                  accessToken
                    ? "Um token já está configurado; ao salvar você irá sobrescrevê-lo."
                    : "Informe o token de acesso gerado na Meta."
                }
                sx={inputFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowAccessToken(!showAccessToken)}
                        edge="end"
                        size="small"
                        sx={{ color: "#667781" }}
                      >
                        {showAccessToken ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ p: 2.5, borderTop: "1px solid #e9edef", gap: 1 }}
          >
            <Button
              onClick={() =>
                !savingAccessToken && setOpenAccessTokenModal(false)
              }
              disabled={savingAccessToken}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#f0f2f5" },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!account) return;
                const companyId =
                  account.company.crm?.company_id || account.company._id;
                if (!companyId) return;
                setSavingAccessToken(true);
                await handleApiCall(
                  async () => {
                    await updateMetaAppConfig(companyId, {
                      access_token: editAccessToken.trim() || undefined,
                    });
                    const updated = await getCompanyData(account.company_id);
                    if (
                      updated.status === "success" &&
                      updated.data &&
                      setAccount
                    ) {
                      const newCompany =
                        updated.data as unknown as Account["company"];
                      setAccount((prev) =>
                        prev ? { ...prev, company: newCompany } : prev,
                      );
                    }
                    return { status: "success" as const };
                  },
                  {
                    onSuccess: () => {
                      showSuccess("ACCESS_TOKEN atualizado com sucesso!");
                      setOpenAccessTokenModal(false);
                    },
                    onError: () => setSavingAccessToken(false),
                    fallbackErrorMessage:
                      "Erro ao atualizar ACCESS_TOKEN. Tente novamente.",
                    successMessage: undefined,
                  },
                );
                setSavingAccessToken(false);
              }}
              disabled={savingAccessToken || !editAccessToken.trim()}
              startIcon={savingAccessToken ? <RefreshIcon /> : <SaveIcon />}
              sx={{
                backgroundColor: "#008069",
                "&:hover": { backgroundColor: "#006d5a" },
                "&:disabled": { backgroundColor: "#cccccc" },
              }}
            >
              {savingAccessToken ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal VERIFY_TOKEN */}
        <Dialog
          open={openVerifyTokenModal}
          onClose={() => !savingVerifyToken && setOpenVerifyTokenModal(false)}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={savingVerifyToken}
          slotProps={{ paper: { sx: dialogPaperSx } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2.5,
              borderBottom: "1px solid #e9edef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SettingsIcon sx={{ color: "#008069", fontSize: "20px" }} />
              <Typography
                variant="h6"
                sx={{ fontSize: "16px", fontWeight: 600, color: "#111b21" }}
              >
                Configurar VERIFY_TOKEN
              </Typography>
            </Box>
            <IconButton
              onClick={() =>
                !savingVerifyToken && setOpenVerifyTokenModal(false)
              }
              disabled={savingVerifyToken}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#e9edef" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#667781", fontSize: "13px", mb: 1 }}
              >
                Token de verificação configurado no webhook da Meta. Ele deve
                ser idêntico ao definido no painel da Meta para validar o
                callback.
              </Typography>
              <TextField
                label="VERIFY_TOKEN"
                value={editVerifyToken}
                onChange={(e) => setEditVerifyToken(e.target.value)}
                placeholder="Ex: meu-token-seguro"
                variant="outlined"
                fullWidth
                disabled={savingVerifyToken}
                type={showVerifyToken ? "text" : "password"}
                helperText={
                  verifyToken
                    ? "Um token já está configurado; ao salvar você irá sobrescrevê-lo."
                    : "Informe o VERIFY_TOKEN usado no webhook da Meta."
                }
                sx={inputFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowVerifyToken(!showVerifyToken)}
                        edge="end"
                        size="small"
                        sx={{ color: "#667781" }}
                      >
                        {showVerifyToken ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ p: 2.5, borderTop: "1px solid #e9edef", gap: 1 }}
          >
            <Button
              onClick={() =>
                !savingVerifyToken && setOpenVerifyTokenModal(false)
              }
              disabled={savingVerifyToken}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#f0f2f5" },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!account) return;
                const companyId =
                  account.company.crm?.company_id || account.company._id;
                if (!companyId) return;
                setSavingVerifyToken(true);
                await handleApiCall(
                  async () => {
                    await updateMetaAppConfig(companyId, {
                      verify_token: editVerifyToken.trim() || undefined,
                    });
                    const updated = await getCompanyData(account.company_id);
                    if (
                      updated.status === "success" &&
                      updated.data &&
                      setAccount
                    ) {
                      const newCompany =
                        updated.data as unknown as Account["company"];
                      setAccount((prev) =>
                        prev ? { ...prev, company: newCompany } : prev,
                      );
                    }
                    return { status: "success" as const };
                  },
                  {
                    onSuccess: () => {
                      showSuccess("VERIFY_TOKEN atualizado com sucesso!");
                      setOpenVerifyTokenModal(false);
                    },
                    onError: () => setSavingVerifyToken(false),
                    fallbackErrorMessage:
                      "Erro ao atualizar VERIFY_TOKEN. Tente novamente.",
                    successMessage: undefined,
                  },
                );
                setSavingVerifyToken(false);
              }}
              disabled={savingVerifyToken || !editVerifyToken.trim()}
              startIcon={savingVerifyToken ? <RefreshIcon /> : <SaveIcon />}
              sx={{
                backgroundColor: "#008069",
                "&:hover": { backgroundColor: "#006d5a" },
                "&:disabled": { backgroundColor: "#cccccc" },
              }}
            >
              {savingVerifyToken ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal APP_IDENTIFY */}
        <Dialog
          open={openAppIdentifyModal}
          onClose={() => !savingAppIdentify && setOpenAppIdentifyModal(false)}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={savingAppIdentify}
          slotProps={{ paper: { sx: dialogPaperSx } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2.5,
              borderBottom: "1px solid #e9edef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SettingsIcon sx={{ color: "#008069", fontSize: "20px" }} />
              <Typography
                variant="h6"
                sx={{ fontSize: "16px", fontWeight: 600, color: "#111b21" }}
              >
                Configurar APP_IDENTIFY
              </Typography>
            </Box>
            <IconButton
              onClick={() =>
                !savingAppIdentify && setOpenAppIdentifyModal(false)
              }
              disabled={savingAppIdentify}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#e9edef" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#667781", fontSize: "13px", mb: 1 }}
              >
                Identificador interno da aplicação configurada na Meta (por
                exemplo, um nome curto ou código do app).
              </Typography>
              <TextField
                label="APP_IDENTIFY"
                value={editAppIdentify}
                onChange={(e) => setEditAppIdentify(e.target.value)}
                placeholder="Ex: virtus_whatsapp_app"
                variant="outlined"
                fullWidth
                disabled={savingAppIdentify}
                helperText={
                  appIdentify
                    ? `APP_IDENTIFY atual: ${appIdentify}`
                    : "Informe o identificador do app na Meta."
                }
                sx={inputFieldSx}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ p: 2.5, borderTop: "1px solid #e9edef", gap: 1 }}
          >
            <Button
              onClick={() =>
                !savingAppIdentify && setOpenAppIdentifyModal(false)
              }
              disabled={savingAppIdentify}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#f0f2f5" },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!account) return;
                const companyId =
                  account.company.crm?.company_id || account.company._id;
                if (!companyId) return;
                setSavingAppIdentify(true);
                await handleApiCall(
                  async () => {
                    await updateMetaAppConfig(companyId, {
                      app_identify: editAppIdentify.trim() || undefined,
                    });
                    const updated = await getCompanyData(account.company_id);
                    if (
                      updated.status === "success" &&
                      updated.data &&
                      setAccount
                    ) {
                      const newCompany =
                        updated.data as unknown as Account["company"];
                      setAccount((prev) =>
                        prev ? { ...prev, company: newCompany } : prev,
                      );
                    }
                    return { status: "success" as const };
                  },
                  {
                    onSuccess: () => {
                      showSuccess("APP_IDENTIFY atualizado com sucesso!");
                      setOpenAppIdentifyModal(false);
                    },
                    onError: () => setSavingAppIdentify(false),
                    fallbackErrorMessage:
                      "Erro ao atualizar APP_IDENTIFY. Tente novamente.",
                    successMessage: undefined,
                  },
                );
                setSavingAppIdentify(false);
              }}
              disabled={savingAppIdentify || !editAppIdentify.trim()}
              startIcon={savingAppIdentify ? <RefreshIcon /> : <SaveIcon />}
              sx={{
                backgroundColor: "#008069",
                "&:hover": { backgroundColor: "#006d5a" },
                "&:disabled": { backgroundColor: "#cccccc" },
              }}
            >
              {savingAppIdentify ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal PHONE_NUMBER_ID */}
        <Dialog
          open={openPhoneNumberIdModal}
          onClose={handleClosePhoneNumberIdModal}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={savingPhoneNumberId}
          slotProps={{ paper: { sx: dialogPaperSx } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2.5,
              borderBottom: "1px solid #e9edef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SettingsIcon sx={{ color: "#008069", fontSize: "20px" }} />
              <Typography
                variant="h6"
                sx={{ fontSize: "16px", fontWeight: 600, color: "#111b21" }}
              >
                Configurar PHONE_NUMBER_ID
              </Typography>
            </Box>
            <IconButton
              onClick={handleClosePhoneNumberIdModal}
              disabled={savingPhoneNumberId}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#e9edef" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#667781", fontSize: "13px", mb: 1 }}
              >
                O PHONE_NUMBER_ID é o ID do número no WhatsApp Cloud API (Meta).
                Use somente se você precisar corrigir manualmente.
              </Typography>
              <TextField
                label="PHONE_NUMBER_ID"
                value={phoneNumberId}
                onChange={(e) => setPhoneNumberId(e.target.value)}
                placeholder="Ex: 516372774902266"
                variant="outlined"
                fullWidth
                disabled={savingPhoneNumberId}
                helperText={
                  phoneNumberIdForVerification
                    ? `PHONE_NUMBER_ID atual: ${phoneNumberIdForVerification}`
                    : "Informe o PHONE_NUMBER_ID"
                }
                sx={inputFieldSx}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ p: 2.5, borderTop: "1px solid #e9edef", gap: 1 }}
          >
            <Button
              onClick={handleClosePhoneNumberIdModal}
              disabled={savingPhoneNumberId}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#f0f2f5" },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSavePhoneNumberId}
              disabled={savingPhoneNumberId || !phoneNumberId.trim()}
              startIcon={savingPhoneNumberId ? <RefreshIcon /> : <SaveIcon />}
              sx={{
                backgroundColor: "#008069",
                "&:hover": { backgroundColor: "#006d5a" },
                "&:disabled": { backgroundColor: "#cccccc" },
              }}
            >
              {savingPhoneNumberId ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal WABA_ID */}
        <Dialog
          open={openWabaModal}
          onClose={handleCloseWabaModal}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={savingWabaId}
          slotProps={{ paper: { sx: dialogPaperSx } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2.5,
              borderBottom: "1px solid #e9edef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SettingsIcon sx={{ color: "#008069", fontSize: "20px" }} />
              <Typography
                variant="h6"
                sx={{ fontSize: "16px", fontWeight: 600, color: "#111b21" }}
              >
                Configurar WABA_ID
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseWabaModal}
              disabled={savingWabaId}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#e9edef" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#667781", fontSize: "13px", mb: 1 }}
              >
                O WhatsApp Business Account ID (WABA_ID) é necessário para
                buscar templates do WhatsApp.
              </Typography>
              <TextField
                label="WABA_ID (WhatsApp Business Account ID)"
                value={wabaId}
                onChange={(e) => setWabaId(e.target.value)}
                placeholder="Ex: 123456789012345"
                variant="outlined"
                fullWidth
                disabled={savingWabaId}
                helperText={
                  currentWabaId
                    ? `WABA_ID atual: ${currentWabaId}`
                    : "Informe o WABA_ID para buscar templates do WhatsApp"
                }
                sx={inputFieldSx}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ p: 2.5, borderTop: "1px solid #e9edef", gap: 1 }}
          >
            <Button
              onClick={handleCloseWabaModal}
              disabled={savingWabaId}
              sx={{
                color: "#667781",
                "&:hover": { backgroundColor: "#f0f2f5" },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveWabaId}
              disabled={savingWabaId || !wabaId.trim()}
              startIcon={savingWabaId ? <RefreshIcon /> : <SaveIcon />}
              sx={{
                backgroundColor: "#008069",
                "&:hover": { backgroundColor: "#006d5a" },
                "&:disabled": { backgroundColor: "#cccccc" },
              }}
            >
              {savingWabaId ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>
      </ContentContainer>

      {/* Botão fixo no rodapé - apenas para ADMINISTRATOR */}
      {isAdministrator && (
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fff",
            position: "sticky",
            bottom: 0,
            zIndex: 11,
            minHeight: "64px",
            maxHeight: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              const companyId =
                account.company.crm?.company_id || account.company._id;
              if (!companyId) return;

              await handleApiCall(
                async () => {
                  await fetchWhatsAppInfo(companyId);

                  // Recarregar os dados da empresa após a atualização
                  try {
                    const updatedCompanyData = await getCompanyData(
                      account.company_id,
                    );
                    if (
                      updatedCompanyData.status === "success" &&
                      updatedCompanyData.data &&
                      setAccount
                    ) {
                      const newCompany =
                        updatedCompanyData.data as unknown as Account["company"];
                      setAccount((prevAccount) => {
                        if (!prevAccount) return prevAccount;
                        return {
                          ...prevAccount,
                          company: {
                            ...prevAccount.company,
                            ...newCompany,
                            _id: prevAccount.company._id || newCompany._id,
                            crm: newCompany.crm || prevAccount.company.crm,
                          },
                        };
                      });
                    }
                  } catch (error) {
                    console.warn("Erro ao recarregar dados da empresa:", error);
                  }

                  return { status: "success" };
                },
                {
                  onSuccess: () => {
                    showSuccess(
                      "Informações da empresa atualizadas com sucesso!",
                    );
                  },
                  onError: () => {
                    // Error handled by hook
                  },
                  fallbackErrorMessage:
                    "Erro ao atualizar informações da empresa. Tente novamente.",
                  successMessage: undefined,
                },
              );
            }}
            startIcon={<RefreshIcon />}
            fullWidth
            sx={{
              backgroundColor: "#008069",
              "&:hover": {
                backgroundColor: "#006d5a",
              },
              "&:disabled": {
                backgroundColor: "#cccccc",
              },
              fontSize: "14px",
              padding: "8px",
              textTransform: "none",
              fontWeight: 500,
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              {refreshingMetaLimits ? "Atualizando…" : "Atualizar Informações"}
            </Typography>
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default CompanySettings;
